import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import i18n from 'src/locales/i18n';
import { HOST_API, AUTH0_API } from 'src/config-global';

// ----------------------------------------------------------------------

interface TokenCache {
  token: string;
  expiresAt: number;
}

let tokenCache: TokenCache | null = null;

let onAuthError: (() => void) | null = null;
let authErrorTriggered = false;

// Handler for the agency-lifecycle 403 (suspended/retired, ADR-0003 §5): the backend tags these with the
// X-Agency-Status header so they're distinguishable from an ordinary permission 403. Wired in the auth
// provider to surface the message and sign the user out, instead of failing every request silently.
let onAgencyBlocked: ((message: string) => void) | null = null;
let agencyBlockedTriggered = false;

// The Auth0 `getAccessTokenSilently` function, registered once where Auth0 is in
// scope (see auth-provider). The request interceptor pulls the token from here, so
// callers no longer have to thread it through every resource call.
let tokenProvider: ((options: any) => Promise<any>) | null = null;

export function registerAuthErrorHandler(handler: (() => void) | null) {
  onAuthError = handler;
}

export function registerAgencyBlockedHandler(handler: ((message: string) => void) | null) {
  onAgencyBlocked = handler;
}

// Fired once per session when the backend reports the caller's agency is suspended/retired. Latched so
// the barrage of 403s a blocked user generates yields a single message + logout, not a storm.
function triggerAgencyBlocked(message: string) {
  if (agencyBlockedTriggered) return;
  agencyBlockedTriggered = true;
  tokenCache = null;
  if (onAgencyBlocked) onAgencyBlocked(message);
}

export function registerTokenProvider(provider: ((options: any) => Promise<any>) | null) {
  tokenProvider = provider;
}

function triggerAuthError() {
  if (authErrorTriggered) return;
  authErrorTriggered = true;
  // Clearing the cache is what de-authenticates the next request now that the
  // token is attached per-request by the interceptor (no shared default header).
  tokenCache = null;
  if (onAuthError) onAuthError();
}

const axiosInstance = axios.create({
  baseURL: HOST_API,
  timeout: 15000,
});

// Request interceptor: attach the Auth0 token (from the registered provider) to
// every request's own headers. Token acquisition + caching lives here now, so no
// caller needs to pass `getAccessTokenSilently`. When no provider is registered
// (e.g. before login), requests go out unauthenticated, exactly as before.
axiosInstance.interceptors.request.use(async (config) => {
  const token = await acquireToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Enhanced response interceptor with better error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      if (error.response.status === 401) {
        triggerAuthError();
      }
      const data = error.response.data as any;
      const message = extractErrorMessage(data, error.response.status);
      // Agency suspended/retired: the backend tags it with X-Agency-Status (header) and a `code` in the
      // body. Surface the message and sign the user out rather than letting every request fail silently.
      const agencyStatus = error.response.headers?.['x-agency-status'];
      if (
        error.response.status === 403 &&
        (agencyStatus || data?.code === 'agency_suspended' || data?.code === 'agency_retired')
      ) {
        triggerAgencyBlocked(message);
      }
      return Promise.reject(new Error(message));
    }

    if (error.request) {
      // Request was made but no response received
      return Promise.reject(new Error('Network error: No response from server'));
    }

    // Something else happened
    return Promise.reject(new Error(error.message || 'An unexpected error occurred'));
  }
);

// Extracts a human-readable message from an Ardalis.Result-shaped error
// body (validationErrors / errors), falling back to plain string bodies or
// `data.message` for non-Result endpoints.
function extractErrorMessage(data: any, status: number): string {
  if (typeof data === 'string' && data.trim()) return data;

  if (data && typeof data === 'object') {
    const validationErrors = Array.isArray(data.validationErrors) ? data.validationErrors : [];
    const validationMessages = validationErrors
      .map((e: any) => translateValidationError(e))
      .filter((m: any): m is string => typeof m === 'string' && m.length > 0);
    if (validationMessages.length) return validationMessages.join('\n');

    const errors = Array.isArray(data.errors) ? data.errors : [];
    const errorMessages = errors.filter(
      (m: any): m is string => typeof m === 'string' && m.length > 0
    );
    if (errorMessages.length) return errorMessages.join('\n');

    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.detail === 'string' && data.detail) return data.detail;
    if (typeof data.title === 'string' && data.title) return data.title;
  }

  return `Server error: ${status}`;
}

// Translates a single Ardalis validation error using its `errorCode` and the
// JSON-serialized parameters carried in `identifier`. Falls back to the raw
// `errorMessage` when no translation key matches.
function translateValidationError(err: any): string {
  if (!err || typeof err !== 'object') return '';

  const code = typeof err.errorCode === 'string' ? err.errorCode : '';
  const fallback = typeof err.errorMessage === 'string' ? err.errorMessage : '';
  if (!code) return fallback;

  const key = `errors.${code}`;
  if (!i18n.exists(key)) return fallback;

  let params: Record<string, unknown> = {};
  if (typeof err.identifier === 'string' && err.identifier) {
    try {
      const parsed = JSON.parse(err.identifier);
      if (parsed && typeof parsed === 'object') params = parsed;
    } catch {
      // identifier wasn't JSON — translate without params
    }
  }

  return i18n.t(key, { ...params, defaultValue: fallback });
}

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// Bare instance for genuinely public (pre-authentication) endpoints. It deliberately
// has NO auth interceptors: the default instance acquires an Auth0 token on every
// request and triggers a login redirect when none exists, which would hijack an
// anonymous page (e.g. the agency portal landing) before it can render.
export const publicAxiosInstance = axios.create({
  baseURL: HOST_API,
  timeout: 15000,
});

export const publicFetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await publicAxiosInstance.get(url, { ...config });

  return res.data;
};

export const deleteResource = async (url: string, data: any | undefined) => {
  try {
    if (data) {
      return await axiosInstance.delete(url, { data });
    }

    return await axiosInstance.delete(url);
  } catch (error) {
    console.error('Error deleting resource:', error);
    throw error;
  }
};

export const createResource = async (url: string, data: any, contentType = 'application/json') => {
  try {
    if (data instanceof FormData) {
      // Let the browser/axios set multipart/form-data with the correct boundary.
      delete axiosInstance.defaults.headers.post['Content-Type'];
      return await axiosInstance.post(url, data);
    }
    axiosInstance.defaults.headers.post['Content-Type'] = contentType;
    return await axiosInstance.post(url, data);
  } catch (error) {
    console.error('Error creating resource:', error);
    throw error;
  }
};

// Returns a valid access token from the registered provider, using the in-memory
// cache (with a 30s expiry buffer) before asking Auth0 for a fresh one. Returns
// null when no provider is registered. On a silent-auth failure it triggers the
// 401/login flow and rethrows so the request fails fast.
async function acquireToken(): Promise<string | null> {
  if (!tokenProvider) return null;

  // Check if we have a valid cached token
  if (tokenCache && tokenCache.expiresAt > Date.now() + 30000) {
    // 30s buffer
    return tokenCache.token;
  }

  try {
    // Get new token
    const { access_token, expires_in } = await tokenProvider({
      authorizationParams: {
        scope: 'read:client_grants',
        audience: AUTH0_API.audience,
      },
      detailedResponse: true,
    });

    if (access_token) {
      // Cache the token with expiration time
      tokenCache = {
        token: access_token,
        expiresAt: Date.now() + expires_in * 1000 - 30000, // 30s buffer
      };
      return access_token;
    }

    return null;
  } catch (error) {
    console.error('Error getting access token:', error);
    triggerAuthError();
    throw error;
  }
}

// Export function to clear token cache (useful for logout)
export function clearTokenCache() {
  tokenCache = null;
  authErrorTriggered = false;
}

export const updateResource = async (url: string, data: any, httpMethod: string = 'put') => {
  try {
    if (httpMethod === 'put') {
      return await axiosInstance.put(url, data);
    }
    return await axiosInstance.patch(url, data);
  } catch (error) {
    console.error('Error updating resource:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/api/auth/me',
    login: '/api/auth/login',
    register: '/api/auth/register',
  },
  user: {
    new: '/api/user/create',
    list: '/api/user/list',
  },

  client: {
    clients: '/api/clients',
    clientsSearch: '/api/clients/search',
    createdRecipient: 'api/clients/add-recipient',
    myRecipients: '/api/clients/me/recipients',
    deleteRecipient: '/api/clients/delete-recipient',
    updateRecipient: '/api/clients',
    getRecipient: '/api/clients',
    processDriverLicense: '/api/client/process-driver-license',
  },
  employee: {
    employees: '/api/employees',
  },
  courier: {
    couriers: '/api/couriers',
    // Public, token-gated courier proof-of-delivery capture (PRD #108, backend #105).
    // BOTH the GET (context) and POST (submit proof) are [AllowAnonymous] — the URL
    // token is the only credential, so these MUST be called with the bare
    // `publicAxiosInstance` (no Auth0 bearer attached).
    capture: (token: string) => `/api/courier/capture/${token}`,
  },
  dispatcher: {
    dispatchers: '/api/dispatchers',
  },
  receptor: {
    receptors: '/api/receptors',
  },
  distributionClientManagement: {
    root: '/api/distribution-clients',
  },
  provider: {
    providers: '/api/providers',
  },
  dispatch: {
    scan: '/api/dispatch/scan',
    create: '/api/dispatch',
    list: '/api/dispatch',
    details: (id: string) => `/api/dispatch/${id}`,
    update: (id: string) => `/api/dispatch/${id}`,
    delete: (id: string) => `/api/dispatch/${id}`,
    sendToSolved: (id: string) => `/api/dispatch/${id}/send-to-solved`,
    generateAerovaradero: (id: string) => `/api/dispatch/${id}/aerovaradero/generate`,
    updateStatus: (id: string) => `/api/dispatch/status/${id}`,
  },
  masterDispatch: {
    create: '/api/master-dispatch',
    list: '/api/master-dispatch',
    details: (id: string) => `/api/master-dispatch/${id}`,
    update: (id: string) => `/api/master-dispatch/${id}`,
    delete: (id: string) => `/api/master-dispatch/${id}`,
    availableDispatches: '/api/master-dispatch/available-dispatches',
  },
  manifest: {
    create: '/api/manifests',
    list: '/api/manifests',
    details: (id: string) => `/api/manifests/${id}`,
    update: (id: string) => `/api/manifests/${id}`,
    delete: (id: string) => `/api/manifests/${id}`,
    receive: (id: string) => `/api/manifests/${id}/receive`,
    receiveByTracking: '/api/manifests/receive-by-tracking',
    complete: (id: string) => `/api/manifests/${id}/complete`,
    approve: (id: string) => `/api/manifests/${id}/approve`,
    reject: (id: string) => `/api/manifests/${id}/reject`,
  },
  reception: {
    create: '/api/receptions',
    list: '/api/receptions',
    details: (id: string) => `/api/receptions/${id}`,
    byManifest: (manifestId: string) => `/api/receptions/by-manifest/${manifestId}`,
    findByTracking: '/api/receptions/find-by-tracking',
    delete: (id: string) => `/api/receptions/${id}`,
  },
  distribution: {
    create: '/api/distributions',
    list: '/api/distributions',
    details: (id: string) => `/api/distributions/${id}`,
    deliver: (id: string) => `/api/distributions/${id}/deliver`,
    cancel: (id: string) => `/api/distributions/${id}/cancel`,
    delete: (id: string) => `/api/distributions/${id}`,
  },
  distributorManifest: {
    list: '/api/distributor-manifests',
    details: (id: string) => `/api/distributor-manifests/${id}`,
    deliver: (id: string) => `/api/distributor-manifests/${id}/deliver`,
    cancel: (id: string) => `/api/distributor-manifests/${id}/cancel`,
    delete: (id: string) => `/api/distributor-manifests/${id}`,
  },
  claim: {
    create: '/api/claims',
    list: '/api/claims',
    details: (id: string) => `/api/claims/${id}`,
    update: (id: string) => `/api/claims/${id}`,
    delete: (id: string) => `/api/claims/${id}`,
    bulkDelete: '/api/claims/bulk',
    uploadFiles: (id: string) => `/api/claims/${id}/files`,
    deleteFile: (claimId: string, fileId: string) => `/api/claims/${claimId}/files/${fileId}`,
  },
  locker: {
    packages: '/api/locker/packages',
    myPackages: '/api/locker/packages/me',
    ship: '/api/locker/packages/ship',
    receive: (id: string) => `/api/locker/packages/${id}/receive`,
    problem: (id: string) => `/api/locker/packages/${id}/problem`,
    update: (id: string) => `/api/locker/packages/${id}`,
    myUpdate: (id: string) => `/api/locker/packages/me/${id}`,
  },
  portal: {
    branding: (slug: string) => `/api/portal/branding/${slug}`,
    selfRegister: '/api/portal/self-register',
    myMemberships: '/api/portal/my-memberships',
    myLockerAddress: '/api/portal/my-locker-address',
    // Unified Client Portal — shipments read-only (issue #80). Allow-list DTO, scoped to the
    // logged-in client by the backend PortalClient policy.
    shipments: '/api/portal/shipments',
    shipment: (id: string) => `/api/portal/shipments/${id}`,
    // Unified Client Portal — passports read-only (issue #82). Status + timeline only (no scan
    // download). Allow-list DTO, scoped to the logged-in client by the backend PortalClient policy.
    passports: '/api/portal/passports',
    passport: (id: string) => `/api/portal/passports/${id}`,
    // Unified Client Portal — remittances read-only (issue #84). Allow-list DTO, scoped to the
    // logged-in client by the backend PortalClient policy.
    remittances: '/api/portal/remittances',
    remittance: (id: string) => `/api/portal/remittances/${id}`,
    // Unified Client Portal — USCIS migration forms read-only (issue #83). Allow-list DTO, scoped to
    // the logged-in client by the backend PortalClient policy. The document endpoint returns a
    // short-lived SAS to the FINAL FILED form PDF only (never a draft/work-in-progress version).
    migrations: '/api/portal/migrations',
    migration: (id: string) => `/api/portal/migrations/${id}`,
    migrationDocument: (id: string) => `/api/portal/migrations/${id}/document`,
    // Unified Client Portal — WhatsApp consent (issue #85). GET returns { optIn, optInAt, waMeLink };
    // POST { optIn, phone? } sets consent for the logged-in client (PortalClient policy).
    whatsAppConsent: '/api/portal/whatsapp-consent',
  },
  report: {
    reports: '/api/reports',
    transactionSummaries: '/api/transaction-summaries',
  },
  dashboard: {
    dashboardTotals: '/api/dashboard/totals',
    dashboardMonthly: '/api/dashboard/monthly-shipments',
    unifiedTotals: '/api/dashboard/unified-totals',
    dailyNetGain: '/api/dashboard/daily-net-gain',
    operational: '/api/dashboard/operational',
  },
  shipping: {
    shippings: '/api/shipments',
    shippingsStatus: '/api/shipments/status',
    shippingsUploadFile: '/api/shipments/upload-files',
    shippingsDeleteFile: '/api/shipments/delete-files',
    shippingsFiles: '/api/shipments/files',
    track: '/api/shipments/track',
    ordersByShipment: (shipmentId: string) => `/api/shipments/${shipmentId}/orders`,
  },
  parcel: {
    parcelUploadFile: '/api/parcels/upload-files',
    parcelDeleteFile: '/api/parcels/delete-files',
    parcelFiles: '/api/parcels/files',
  },
  agency: {
    root: '/api/agency',
    settings: '/api/agency',
    saveBranding: '/api/agency/branding',
    saveServices: '/api/agency/services',
    savePackageTypes: '/api/agency/package-types',
    saveDeliveryTypes: '/api/agency/delivery-types',
    saveRemittanceDeliveryTypes: '/api/agency/remittance-delivery-types',
    saveRemittancePickupLocations: '/api/agency/remittance-pickup-locations',
    parcelContentTypes: '/api/parcel-content-type',
    parcelContentTypesRange: '/api/parcel-content-type-range',
    contentCategories: '/api/content-category',
    products: '/api/agency-product',
    savePickupLocation: '/api/agency/pickup-locations',
    saveProvincia: '/api/agency/provincia',
    saveMunicipio: '/api/agency/municipio',
    savePayment: '/api/agency/payment-methods',
    saveMigrationClasification: '/api/agency/migration-clasification',
    saveProfession: '/api/agency/profession',
    saveEyesColor: '/api/agency/eyes-color',
    saveSkinColor: '/api/agency/skin-color',
    saveHairColor: '/api/agency/hair-color',
    saveReason: '/api/agency/reason',
  },
  passport: {
    passports: '/api/passports',
    draftPassports: '/api/passports/draft',
    passportService: '/api/passport-category',
    passportsUploadFile: '/api/passports/upload-files',
    passportsDeleteFile: '/api/passports/delete-files',
    passportsFiles: '/api/passports/files',
    generateEncodedPassports: '/api/passports/generate-encoded',
    expiring: '/api/passports/expiring',
    processPhoto: '/api/passport-scan/process',
    createWithClient: '/api/passports/create-with-client',
  },
  remittance: {
    remittances: '/api/remittances',
    remittanceStatus: '/api/remittance-status',
    remittanceUploadFile: '/api/remittance/upload-files',
    remittanceDeleteFile: '/api/remittance/delete-files',
    remittanceFiles: '/api/remittance/files',
    remittanceDeliveryTypes: '/api/remittance-delivery-types',
  },
  migration: {
    migrations: '/api/migrations',
    draftMigrations: '/api/migrations/draft',
    migrationStatus: '/api/migration-status',
    generateEncodedMigration: '/api/migrations/generate-encoded',
    migrationsUploadFile: '/api/migrations/upload-files',
    migrationsDeleteFile: '/api/migrations/delete-files',
    migrationsFiles: '/api/migrations/files',
    saveMigrationFormsSettings: '/api/migration-form-settings',
    migrationFormsSettings: '/api/migration-form-settings',
  },
  notifications: {
    bulkSms: '/api/notifications/bulk-sms',
  },
  tracking: {
    byTrackingNumber: '/api/tracking/by-tracking-number',
    byShipment: '/api/tracking/by-shipment',
    byShipmentTracking: '/api/tracking/by-shipment-tracking',
    byManifestTracking: '/api/tracking/by-manifest-tracking',
  },
  distributionClient: {
    import: '/api/distribution-client/manifests',
    list: '/api/distribution-client/manifests',
    details: (id: string) => `/api/distribution-client/manifests/${id}`,
    profile: '/api/distribution-client/profile',
  },
  upload: '/api/upload',
  admin: {
    // Backend route is /api/platform/* — the Functions host reserves the "admin" route segment.
    agencies: '/api/platform/agencies',
    agencyInsights: '/api/platform/agencies/insights',
    health: '/api/platform/health',
    agencyDefaults: '/api/platform/agency-defaults',
    ownerResetPassword: (number: string) => `/api/platform/agencies/${number}/owner/reset-password`,
    ownerSetPassword: (number: string) => `/api/platform/agencies/${number}/owner/set-password`,
    // Assign / change an agency's portal slug (the /portal/{slug} URL). Gated by agency:onboard.
    agencySlug: (number: string) => `/api/platform/agencies/${number}/slug`,
    suspendAgency: (number: string) => `/api/platform/agencies/${number}/suspend`,
    reactivateAgency: (number: string) => `/api/platform/agencies/${number}/reactivate`,
    retireAgency: (number: string) => `/api/platform/agencies/${number}/retire`,
    // Slice 5 (#50): read-only view-as, audit trail, platform-admin management.
    agencyOverview: (number: string) => `/api/platform/agencies/${number}/overview`,
    audit: '/api/platform/audit',
    admins: '/api/platform/admins',
    removeAdmin: (userId: string) => `/api/platform/admins/${encodeURIComponent(userId)}`,
    // Slice 1 (#57): global rate card + per-agency billing profiles (gated by agency:billing).
    rateCard: '/api/platform/billing/rate-card',
    billingProfiles: '/api/platform/billing/agencies',
    billingProfile: (number: string) => `/api/platform/billing/agencies/${number}/profile`,
    // Slices 5 & 6 (#61): platform invoice management + monthly billing run.
    agencyInvoices: (number: string) => `/api/platform/billing/agencies/${number}/invoices`,
    agencyInvoice: (number: string, invoiceNumber: string) =>
      `/api/platform/billing/agencies/${number}/invoices/${invoiceNumber}`,
    issueInvoice: (number: string, invoiceNumber: string) =>
      `/api/platform/billing/agencies/${number}/invoices/${invoiceNumber}/issue`,
    addInvoiceAdjustment: (number: string, invoiceNumber: string) =>
      `/api/platform/billing/agencies/${number}/invoices/${invoiceNumber}/adjustments`,
    markInvoicePaid: (number: string, invoiceNumber: string) =>
      `/api/platform/billing/agencies/${number}/invoices/${invoiceNumber}/pay`,
    voidInvoice: (number: string, invoiceNumber: string) =>
      `/api/platform/billing/agencies/${number}/invoices/${invoiceNumber}/void`,
    billingOverdue: '/api/platform/billing/overdue',
    billingRun: '/api/platform/billing/run',
  },
  // Agency billing slice 3 (#59): owner-facing read-only billing. Backend route owner/billing/*,
  // gated by the `read:billing` permission for the agency owner.
  ownerBilling: {
    accrual: '/api/owner/billing/accrual',
    // Slice 6 (#62): owner-facing list of their own issued/paid invoices.
    invoices: '/api/owner/billing/invoices',
  },
  // Owner Daily Cash Close (spec #122, slice #123). Backend route owner/cash-close, gated by OnlyOwners.
  ownerCashClose: {
    root: '/api/owner/cash-close',
  },
};
